import { GameIcon, StatIcon } from "../assets/gameIcons.js";
import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useState } from "react";
import { pick, useT } from "../i18n/index.js";
import { Sheet } from "../base/panels/shared.js";
import { Button } from "../ui/Button.js";
import { formatCompact } from "../ui/Counter.js";
import { Timer } from "../ui/Timer.js";
import { navigate } from "../router.js";
import { useSession } from "../state/session.js";
import { requestScope } from "../api/scope.js";
import { SquadPreview } from "./SquadPreview.js";
import { worldApi } from "./api.js";
import { marchOutcomeText, useWorld } from "./state.js";
import { kindName } from "./TileSheet.js";
import type { MarchView } from "./types.js";
import { RevengeButton } from "./RevengeButton.js";

function lootText(m: MarchView, t: ReturnType<typeof useT>): string {
  const loot = m.result?.loot;
  if (!loot) return "";
  const parts: string[] = [];
  const bundle = loot as { resources?: Record<string, number>; items?: { itemId: string; qty: number }[]; credits?: number };
  const res: Record<string, number> = bundle.resources ?? (loot as Record<string, number>);
  for (const [k, v] of Object.entries(res)) if (typeof v === "number" && v > 0) parts.push(`${formatCompact(v)} ${t(`res.${k}`)}`);
  const items = Array.isArray(bundle.items) ? bundle.items : [];
  for (const i of items) parts.push(`${i.qty}× ${i.itemId.replace("itm.", "")}`);
  if (typeof bundle.credits === "number" && bundle.credits > 0) parts.push(`${bundle.credits} ₵`);
  return parts.join(" · ");
}

/** Active and recent marches with timers, results, report links and recall (R42). */
export function MarchList() {
  const t = useT();
  const me = useWorld((s) => s.me);
  const recall = useWorld((s) => s.recall);
  const setPanel = useWorld((s) => s.setPanel);
  const setJump = useWorld((s) => s.setJump);
  const [recent, setRecent] = useState<MarchView[]>([]);
  useEffect(() => {
    const generation = requestScope().generation; let active = true;
    void worldApi.marches().then((rows) => { if (active && generation === requestScope().generation) setRecent(rows); }, () => undefined);
    return () => { active = false; };
  }, [me?.marches.length]);
  if (!me) return null;
  const rows = [...me.marches, ...recent.filter((r) => r.state === "done" && !me.marches.some((m) => m.id === r.id))].slice(0, 20);
  return (
    <Sheet title={`${t("world.marches")} ${me.marches.filter((m) => m.action !== "scout").length}/${me.marchSlots}`} onClose={() => setPanel("none")} testId="march-list">
      {rows.length === 0 ? <p className="dim">{t("world.noMarches")}</p> : null}
      {rows.map((m) => (
        <div key={m.id} className="list-row march-list-entry" data-testid={`march-${m.id}`}>
          <span className="grow">
            <strong>
              {t(`world.action.${m.action}`)} · {kindName(m.targetKind, t)}
            </strong>{" "}
            <button type="button" className="plain mono" onClick={() => setJump(m.to)}>
              {m.to.x},{m.to.y}
            </button>
            <div className="hint">
              {m.state === "done" ? (
                <span data-testid="march-result">{marchOutcomeText(m)}{lootText(m, t) ? ` · ${lootText(m, t)}` : ""}</span>
              ) : (
                <>
                  {t(`world.state.${m.state}`)} · <FunctionalIcon name="clock" /> <Timer finishesAt={m.state === "marching" ? m.arrivesAt : (m.endsAt ?? m.arrivesAt)} />
                  {m.result?.outcome && m.state === "returning" ? ` · ${marchOutcomeText(m)}` : ""}
                </>
              )}
            </div>
          </span>
          <SquadPreview squad={m.squad} />
          {m.state !== "done" ? <Button onClick={() => useWorld.getState().selectMarch(m.id)} data-testid="march-inspect"><FunctionalIcon name="location" /> {t("world.march.showColumn")}</Button> : null}
          {m.result?.battleId ? (
            <Button style={{ minHeight: 32 }} onClick={() => navigate(`/app/battle/${m.result!.battleId}`)} data-testid="march-report">
              <FunctionalIcon name="play" /> {t("world.report")}
            </Button>
          ) : null}
          {m.state === "marching" || m.state === "gathering" ? (
            <Button style={{ minHeight: 32 }} onClick={() => void recall(m.id)} data-testid="march-recall">
              <FunctionalIcon name="undo" /> {t("world.recall")}
            </Button>
          ) : null}
        </div>
      ))}
    </Sheet>
  );
}

const FIND_KINDS = ["node", "pack", "elite", "hive", "ruin", "camp", "vault", "boss"] as const;

/** Find nearest by kind, search a player, jump to coordinates (R41). */
export function FindPanel() {
  const t = useT();
  const setPanel = useWorld((s) => s.setPanel);
  const setJump = useWorld((s) => s.setJump);
  const select = useWorld((s) => s.select);
  const [name, setName] = useState("");
  const [coords, setCoords] = useState("");
  const [level, setLevel] = useState("");
  const go = (x: number, y: number, tile: Parameters<typeof select>[0] extends infer S ? (S extends { tile: infer T } ? T : never) : never) => {
    setJump({ x, y });
    select({ x, y, tile });
    setPanel("none");
  };
  const nearest = async (kind: string) => {
    const tile = await worldApi.nearest(kind, level ? { level: Number(level) } : {}).catch(() => null);
    if (!tile) return;
    go(tile.x, tile.y, tile);
  };
  const search = async () => {
    const tile = await worldApi.search(name.trim()).catch(() => null);
    if (tile) go(tile.x, tile.y, tile);
  };
  const jumpCoords = () => {
    const m = /^\s*(\d+)\s*[,;\s]\s*(\d+)\s*$/.exec(coords);
    if (!m) return;
    const x = Number(m[1]);
    const y = Number(m[2]);
    setJump({ x, y });
    select({ x, y, tile: null });
    setPanel("none");
  };
  return (
    <Sheet title={t("world.find")} onClose={() => setPanel("none")} testId="find-panel">
      <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
        {FIND_KINDS.map((k) => (
          <Button key={k} style={{ minHeight: 34 }} onClick={() => void nearest(k)} data-testid={`find-${k}`}>
            {kindName(k, t)}
          </Button>
        ))}
        <input className="field" style={{ width: 90 }} placeholder={t("world.level")} value={level} onChange={(e) => setLevel(e.target.value.replace(/\D/g, ""))} data-testid="find-level" />
      </div>
      <div className="row" style={{ gap: 6, marginTop: 12 }}>
        <input className="field grow" placeholder={t("world.searchPlayer")} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void search()} data-testid="search-name" />
        <Button variant="primary" style={{ minHeight: 34 }} onClick={() => void search()} data-testid="search-go">
          <FunctionalIcon name="search" />
        </Button>
      </div>
      <div className="row" style={{ gap: 6, marginTop: 8 }}>
        <input className="field grow mono" placeholder="x,y" value={coords} onChange={(e) => setCoords(e.target.value)} onKeyDown={(e) => e.key === "Enter" && jumpCoords()} data-testid="jump-coords" />
        <Button style={{ minHeight: 34 }} onClick={jumpCoords} data-testid="jump-go">
          <FunctionalIcon name="location" />
        </Button>
      </div>
    </Sheet>
  );
}

export function BookmarksPanel() {
  const t = useT();
  const me = useWorld((s) => s.me);
  const run = useWorld((s) => s.run);
  const setPanel = useWorld((s) => s.setPanel);
  const setJump = useWorld((s) => s.setJump);
  if (!me) return null;
  return (
    <Sheet title={`${t("world.bookmarks")} ${me.bookmarks.length}/${me.bookmarkLimit}`} onClose={() => setPanel("none")} testId="bookmarks-panel">
      {me.bookmarks.length === 0 ? <p className="dim">{t("world.noBookmarks")}</p> : null}
      {me.bookmarks.map((b) => (
        <div key={b.id} className="row list-row" data-testid={`bookmark-${b.id}`}>
          <button type="button" className="plain grow" style={{ textAlign: "left" }} onClick={() => { setJump({ x: b.x, y: b.y }); setPanel("none"); }}>
            <strong>{b.name}</strong> <span className="mono hint">{b.x},{b.y}</span>
          </button>
          <Button style={{ minHeight: 32 }} onClick={() => void run(() => worldApi.removeBookmark(b.id))} data-testid="bookmark-remove">
            <FunctionalIcon name="close" />
          </Button>
        </div>
      ))}
    </Sheet>
  );
}

export function ScoutsPanel() {
  const t = useT();
  const scouts = useWorld((s) => s.scouts);
  const refresh = useWorld((s) => s.refreshScouts);
  const setPanel = useWorld((s) => s.setPanel);
  const setJump = useWorld((s) => s.setJump);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  return (
    <Sheet title={t("world.scouts")} onClose={() => setPanel("none")} testId="scouts-panel">
      {!scouts || scouts.length === 0 ? <p className="dim">{t("world.noScouts")}</p> : null}
      {scouts?.map((r) => {
        const rep = r.report as { owner?: { name: string }; hq?: number; power?: number; resources?: Record<string, number>; squadSize?: number; garrison?: unknown[]; hpPct?: number };
        return (
          <div key={r.id} className="list-row" data-testid={`scout-${r.id}`}>
            <div className="row">
              <strong className="grow">
                {rep.owner?.name ?? kindName(String((r.report as { kind?: string }).kind ?? "?"), t)}{" "}
                <button type="button" className="plain mono" onClick={() => setJump({ x: r.x, y: r.y })}>
                  {r.x},{r.y}
                </button>
              </strong>
              <span className="badge">{t("world.detail", { n: r.detail })}</span>
            </div>
            <div className="hint">
              {rep.hq !== undefined ? <>HQ {rep.hq} · <StatIcon stat="power" /> {formatCompact(rep.power ?? 0)}</> : null}
              {rep.resources ? ` · ${Object.entries(rep.resources).map(([k, v]) => `${formatCompact(v)} ${k}`).join(" ")}` : ""}
              {rep.squadSize !== undefined ? ` · ${t("world.squadSize", { n: rep.squadSize })}` : ""}
              {rep.garrison ? ` · ${t("world.garrisonKnown")}` : ""}
              {rep.hpPct !== undefined ? ` · HP ${Math.round(rep.hpPct)}%` : ""}
              {r.detail === 0 ? t("world.scoutBlocked") : ""}
            </div>
          </div>
        );
      })}
    </Sheet>
  );
}

export function BossPanel() {
  const t = useT();
  const boss = useWorld((s) => s.boss);
  const refresh = useWorld((s) => s.refreshBoss);
  const setPanel = useWorld((s) => s.setPanel);
  const setJump = useWorld((s) => s.setJump);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  return (
    <Sheet title={t("world.boss")} onClose={() => setPanel("none")} testId="boss-panel">
      {!boss ? <p className="dim">{t("common.loading")}</p> : boss.active ? (
        <>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="badge warn">HP {Math.round(boss.hpPct)}%</span>
            <span className="badge">
              <FunctionalIcon name="clock" /> <Timer finishesAt={boss.windowUntil ?? 0} />
            </span>
            <Button style={{ minHeight: 32 }} onClick={() => { setJump({ x: boss.x ?? 0, y: boss.y ?? 0 }); setPanel("none"); }} data-testid="boss-jump">
              <FunctionalIcon name="location" /> {boss.x},{boss.y}
            </Button>
          </div>
          <div className="hint" style={{ marginTop: 8 }}>{t("world.boss.myDamage", { n: formatCompact(boss.myDamage) })}</div>
          {boss.ranking.map((r, i) => (
            <div key={r.accountId} className="row list-row">
              <span className="mono" style={{ width: 28 }}>{i + 1}.</span>
              <span className="grow">{r.name}</span>
              <span className="mono">{formatCompact(r.damage)}</span>
            </div>
          ))}
        </>
      ) : (
        <p className="dim">{boss.nextAt ? <>{t("world.boss.next")} <Timer finishesAt={boss.nextAt} /></> : t("world.boss.none")}</p>
      )}
    </Sheet>
  );
}

export function AttacksPanel() {
  const t = useT();
  const attacks = useWorld((s) => s.attacks);
  const refresh = useWorld((s) => s.refreshAttacks);
  const setPanel = useWorld((s) => s.setPanel);
  const account = useSession((s) => s.account);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  return (
    <Sheet title={t("world.attacks")} onClose={() => setPanel("none")} testId="attacks-panel">
      {!attacks || attacks.length === 0 ? <p className="dim">{t("world.noAttacks")}</p> : null}
      {attacks?.map((a) => {
        const mine = a.attackerId === account?.id;
        return (
          <div key={a.id} className="attack-report-entry" data-testid={`attack-${a.id}`}><div className="row">
            <span className="grow">
              <strong><GameIcon id={mine ? "ui.role.attacker" : "emote.shield"} /> {mine ? a.defenderName : a.attackerName}</strong>
              <div className="hint">
                {a.won ? t("world.outcome.won") : t("world.outcome.lost")}
                {Object.keys(a.loot).length ? ` · ${Object.entries(a.loot).map(([k, v]) => `${formatCompact(v)} ${t(`res.${k}`)}`).join(" ")}` : ""}
              </div>
            </span>
            <Button aria-label={t("battle.replay")} onClick={() => navigate(`/app/battle/${a.battleId}`)}>
              <FunctionalIcon name="play" />
            </Button></div>
            {!mine ? <RevengeButton battleId={a.battleId} expiresAt={a.revengeUntil} /> : null}
          </div>
        );
      })}
    </Sheet>
  );
}

export { pick };
